import { LOCALE_ID, NgModule, Provider } from '@angular/core';
import { GridsterModule } from 'angular-gridster2';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CoreModule } from './@core/core.module';
import { ThemeModule } from './@theme/theme.module';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import {
  NbAlertModule,
  NbButtonModule,
  NbCardModule,
  NbChatModule,
  NbCheckboxModule,
  NbDatepickerModule,
  NbDialogModule,
  NbIconModule,
  NbInputModule,
  NbLayoutModule,
  NbSelectModule,
  NbListModule,
  NbSpinnerModule,
  NbThemeModule,
  NbMenuModule,
  NbSidebarModule,
  NbToastrModule,
  NbTreeGridModule,
  NbWindowModule,
  NbButtonGroupModule,
  NbProgressBarModule,
  NbTooltipModule,
  NbFormFieldModule,
  NbRadioModule,
  NbToggleModule,
  NbPopoverModule,
  NbAutocompleteModule,
  NbBadgeModule,
  NbTagModule,
  NbCalendarModule
} from '@nebular/theme';
import { NbAuthModule, NB_AUTH_TOKEN_INTERCEPTOR_FILTER } from '@nebular/auth';
import { LetDirective } from '@ngrx/component';

import { LoggedInGuard } from './deebr/guard/logged-in.guard';
import { DeebrLoginComponent } from './deebr/component/auth/deebr-login/deebr-login.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DashboardComponent } from './deebr/component/pages/dashboard/dashboard.component';
import { HttpRequest, HTTP_INTERCEPTORS } from '@angular/common/http';
import { BearerInterceptor } from './deebr/interceptor/bearer.interceptor';
import { BaseChartDirective, provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { NgxSliderModule } from '@angular-slider/ngx-slider';
import { CampaignComponent } from './deebr/component/pages/campaign/campaign.component';
import { TreemapComponent } from './deebr/component/treemap/treemap.component';
import { DicoService } from './deebr/service/dico.service';
import { CampaignSegmentEditorComponent } from './deebr/component/campaign-segment-editor/campaign-segment-editor.component';
import localeFr from '@angular/common/locales/fr';
import { DecimalPipe, registerLocaleData } from '@angular/common';
import { MailsService } from './deebr/service/mails.service';
import { LogoutComponent } from './deebr/component/auth/logout/logout.component';
import { VennDiagrammComponent } from './deebr/component/venn-diagramm/venn-diagramm.component';
import {
  NgbDatepickerModule,
  NgbDropdownModule,
  NgbPaginationModule,
  NgbProgressbarModule,
  NgbToastModule,
  NgbTypeaheadModule,
} from '@ng-bootstrap/ng-bootstrap';
import { ResetPasswordComponent } from './deebr/component/auth/reset-password/reset-password.component';
import { RequestPasswordComponent } from './deebr/component/auth/request-password/request-password.component';
import { TableComponent } from './deebr/component/table/table.component';
import { ReportComponent } from './deebr/component/pages/report/report.component';
import { ReportStore } from './deebr/component/pages/report/report.store';
import { UserStore } from './deebr/store/user.store';
import { GeneratorEditComponent } from './deebr/component/pages/generator/generator-edit.component';
import { GeneratorListComponent } from './deebr/component/pages/generator/generator-list.component';
import { EmailStore } from './deebr/component/pages/generator/email.store';
import { ImageModalComponent } from './deebr/component/image-modal/image-modal.component';
import { EmailPreviewPopupComponent } from './deebr/component/pages/generator/email-preview-popup/email-preview-popup.component';
import { ImageChoiceComponent } from './deebr/component/image-choice/image-choice.component';
import Hotjar from '@hotjar/browser';
import { TemplateModalComponent } from './deebr/component/template-modal/template-modal.component';
import { FireflyChoiceComponent } from './deebr/component/firefly-choice/firefly-choice.component';
import { DragDropProductsComponent } from './deebr/component/drag-drop-products/drag-drop-products.component';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { ImageToGifDialogComponent } from './deebr/component/pages/generator/image-to-gif-dialog/image-to-gif-dialog.component'
import { ImageAmbianceDialogComponent } from './deebr/component/pages/generator/image-ambiance-dialog/image-ambiance-dialog.component'
import { CreateGifProductComponent } from './deebr/component/create-gif-product/create-gif-product.component';
import { ResizeImageModalComponent } from './deebr/component/pages/generator/resize-image-modal/resize-image-modal.component';
import { ProductRecommandationComponent } from './deebr/component/pages/product-recommandation/product-recommandation.component';
import { WriteOnImageComponent } from './deebr/component/write-on-image/write-on-image.component';
import { WriteCtaOnImageComponent } from './deebr/component/write-cta-on-image/write-cta-on-image.component';

import { FolderViewComponent } from './deebr/component/pages/generator/folder-view/folder-view.component';
import { FolderTreeSelectComponent } from './deebr/component/pages/generator/folder-tree-select/folder-tree-select.component';
import { CreateEmailFromTemplateComponent } from './deebr/component/create-email-from-template/create-email-from-template.component';
import { GeneratorEditV2Component } from './deebr/component/pages/generator/generator-edit-v2.component';
import { CategorySelectionModalComponent } from './deebr/component/pages/generator/category-selection-modal/category-selection-modal.component';
import { BackupSelectionModalComponent } from './deebr/component/pages/generator/backup-selection-modal/backup-selection-modal.component';
import { DeebrProfileComponent } from './deebr/component/pages/profile/deebr-profile.component';
import { Deebr2faResetComponent } from './deebr/component/auth/deebr-2fa-reset/deebr-2fa-reset.component';
import { Deebr2faActivateComponent } from './deebr/component/auth/deebr-2fa-activate/deebr-2fa-activate.component';
import { PlanningCampaignComponent } from './deebr/component/pages/planning-campaign/planning-campaign.component';
import { CampaignSidebarComponent } from './deebr/component/pages/planning-campaign/campaign-sidebar.component';
import { SegmentMatrixComponent } from './deebr/component/pages/planning-campaign/segment-matrix.component';
import { CalendarModule, DateAdapter } from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns';
import { DynamicImageManagementComponent } from './deebr/component/pages/generator/dynamic-image-management/dynamic-image-management.component';
import { AutomationComponent } from './deebr/component/pages/automation/automation.component';
import { AutomationValidationComponent } from './deebr/component/pages/automation-validation/automation-validation.component';
import { ModuleMenuComponent } from './deebr/component/pages/modules/module-menu/module-menu.component';
import { ModuleEditComponent } from './deebr/component/pages/modules/module-edit/module-edit.component';
import { ModuleListComponent } from './deebr/component/pages/modules/module-list/module-list.component';

const siteId = 3757797; // Remplacez ceci par votre propre siteId
const hotjarVersion = 6; // Utilisez la version Hotjar que vous souhaitez

Hotjar.init(siteId, hotjarVersion);

registerLocaleData(localeFr, 'fr');


let providers: Provider[] = [
  LoggedInGuard,
  DicoService,
  MailsService,
  DecimalPipe,
  { provide: LOCALE_ID, useValue: 'fr-FR' },
  {
    provide: NB_AUTH_TOKEN_INTERCEPTOR_FILTER,
    useValue: function (req: HttpRequest<any>) {
      if (req.url === '/api/token/refresh/' || req.url === '/api/token/') {
        return true;
      }
      return false;
    },
  },
  { provide: HTTP_INTERCEPTORS, useClass: BearerInterceptor, multi: true },
  ReportStore,
  EmailStore,
  UserStore,
  provideCharts(withDefaultRegisterables())
];



@NgModule({
  declarations: [
    AppComponent,
    DeebrLoginComponent,
    DashboardComponent,
    CampaignComponent,
    ReportComponent,
    GeneratorEditComponent,
    GeneratorListComponent,
    ModuleListComponent,
    ModuleEditComponent,
    TreemapComponent,
    CampaignSegmentEditorComponent,
    LogoutComponent,
    VennDiagrammComponent,
    ResetPasswordComponent,
    RequestPasswordComponent,
    TableComponent,
    ImageModalComponent,
    EmailPreviewPopupComponent,
    ImageChoiceComponent,
    TemplateModalComponent,
    FireflyChoiceComponent,
    DragDropProductsComponent,
    ImageToGifDialogComponent,
    ImageAmbianceDialogComponent,
    CreateGifProductComponent,
    ResizeImageModalComponent,
    ProductRecommandationComponent,
    WriteOnImageComponent,
    WriteCtaOnImageComponent,
    FolderViewComponent,
    FolderTreeSelectComponent,
    CreateEmailFromTemplateComponent,
    GeneratorEditV2Component,
    DeebrProfileComponent,
    Deebr2faResetComponent,
    Deebr2faActivateComponent,
    PlanningCampaignComponent,
    CampaignSidebarComponent,
    DynamicImageManagementComponent,
    AutomationComponent,
    AutomationValidationComponent,
  ],
  imports: [
    DragDropModule,
    GridsterModule,
    NbCardModule,
    NbTooltipModule,
    BrowserModule,
    AppRoutingModule,
    NbChatModule,
    NbThemeModule.forRoot(),
    NbDatepickerModule.forRoot(),
    NbToastrModule.forRoot(),
    NbSidebarModule.forRoot(),
    NbMenuModule.forRoot(),
    NbTagModule,
    NbWindowModule,
    LetDirective,
    CoreModule,
    ThemeModule,
    NbTreeGridModule,
    BrowserAnimationsModule,
    NbAlertModule,
    NbInputModule,
    NbButtonModule,
    NbLayoutModule,
    NbCheckboxModule,
    FormsModule,
    NbIconModule,
    BaseChartDirective,
    NgxSliderModule,
    NbBadgeModule,
    NbProgressBarModule,
    NbAutocompleteModule,
    ReactiveFormsModule,
    NbSelectModule,
    NbListModule,
    NbSpinnerModule,
    NgbPaginationModule,
    NgbDatepickerModule,
    NgbTypeaheadModule,
    NgbToastModule,
    NgbDropdownModule,
    NbDialogModule.forRoot(),
    NbButtonGroupModule,
    NgbProgressbarModule,
    NbFormFieldModule,
    NbPopoverModule,
    NbCalendarModule,
    NbRadioModule,
    NbToggleModule,
    CategorySelectionModalComponent,
    BackupSelectionModalComponent,
    ModuleMenuComponent,
    SegmentMatrixComponent,
    CalendarModule.forRoot({
      provide: DateAdapter,
      useFactory: adapterFactory,
    }),
  ],
  providers: providers,
  bootstrap: [AppComponent],
})
export class AppModule { }
